import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';

export interface SelectedDataDialogData {
  title: string;
  data: any[];
  columnName?: string;
  secondColumnName?: string;
}

@Component({
  selector: 'app-selected-data-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule
  ],
  template: `
    <div class="app-dialog selected-data-dialog">
      <div class="app-dialog__heading">
        <button mat-icon-button type="button" class="selected-data-dialog-close" aria-label="Close" (click)="onClose()">
          <mat-icon svgIcon="close"></mat-icon>
        </button>
        <div class="app-dialog__heading-copy">
          <span class="app-dialog__eyebrow">Selected data</span>
          <div class="app-dialog__title-row">
            <div class="app-dialog__title-copy">
              <h2 mat-dialog-title class="app-dialog__title">{{ data.title }}</h2>
              <p>Review the selected rows and export them if needed.</p>
            </div>
          </div>
        </div>
      </div>
      
      <mat-dialog-content class="app-dialog__content dialog-content">
        <div class="data-info" *ngIf="data.data.length > 0">
          <p class="selection-count">{{ data.data.length }} rows selected</p>
        </div>
        
        <div class="table-container" *ngIf="data.data.length > 0">
          <table mat-table [dataSource]="data.data" class="data-table">
            <!-- Dynamic columns -->
            <ng-container *ngFor="let column of displayedColumns" [matColumnDef]="column">
              <th mat-header-cell *matHeaderCellDef>{{ column }}</th>
              <td mat-cell *matCellDef="let row">
                <div class="cell-content">{{ formatCellValue(row[column]) }}</div>
              </td>
            </ng-container>
            
            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>
        </div>
        
        <div class="no-data" *ngIf="data.data.length === 0">
          <mat-icon>info</mat-icon>
          <p>No data selected</p>
        </div>
      </mat-dialog-content>
      
      <mat-dialog-actions class="app-dialog__actions dialog-actions">
        <button mat-raised-button (click)="onClose()">Close</button>
        <button mat-raised-button color="primary" (click)="onExport()" *ngIf="data.data.length > 0">
          <mat-icon>download</mat-icon>
          Export CSV
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .selected-data-dialog {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .selected-data-dialog-close {
      position: absolute !important;
      top: 12px !important;
      right: 16px !important;
      z-index: 1;
      color: var(--text-secondary);
    }

    .dialog-content {
      flex: 1;
      padding: 24px !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .data-info {
      margin-bottom: 16px;
    }

    .selection-count {
      font-size: 14px;
      color: var(--text-secondary, #666);
      margin: 0;
    }

    .table-container {
      flex: 1;
      min-height: 0;
      overflow: auto;
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 8px;
    }

    .data-table {
      width: 100%;
    }

    // Header row styling - matching shared table styles
    .data-table .mat-mdc-header-row {
      background-color: var(--ps-digital-blue, #0080FF) !important;
    }

    // Column width management for data tables with scrollable cells
    .data-table .mat-mdc-header-cell {
      min-width: 150px;
      max-width: 500px;
      white-space: nowrap;
      background-color: var(--ps-digital-blue, #0080FF) !important;
      color: white !important;
      font-weight: 600 !important;
      position: sticky;
      top: 0;
      z-index: 10;
      font-size: 16px !important;
      letter-spacing: 0.5px !important;
    }
    
    .data-table .mat-mdc-cell {
      min-width: 150px;
      max-width: 500px;
      max-height: 120px;
      padding: 0 !important;
      vertical-align: top;
    }

    .cell-content {
      display: block;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 16px;
      word-break: break-word;
      box-sizing: border-box;
    }
    
    // Custom scrollbar styling
    .cell-content::-webkit-scrollbar {
      width: 6px;
    }
    
    .cell-content::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    .cell-content::-webkit-scrollbar-thumb {
      background: #888;
      border-radius: 3px;
    }
    
    .cell-content::-webkit-scrollbar-thumb:hover {
      background: #555;
    }

    .data-table tr:hover {
      background-color: var(--hover-color, #f9f9f9);
    }

    .no-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      color: var(--text-secondary, #666);
    }

    .no-data mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }

    .dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid var(--border-color, #e0e0e0);
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }
  `]
})
export class SelectedDataDialogComponent {
  displayedColumns: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<SelectedDataDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SelectedDataDialogData
  ) {
    // Get columns from first data row
    if (this.data.data.length > 0) {
      this.displayedColumns = Object.keys(this.data.data[0]);
    }
  }

  formatCellValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onExport(): void {
    if (this.data.data.length === 0) return;

    // Convert to CSV
    const headers = this.displayedColumns.join(',');
    const rows = this.data.data.map(row => 
      this.displayedColumns.map(col => {
        const value = row[col];
        // Escape values with commas or quotes
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    
    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `selected_data_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
